<script lang="ts" setup>
// Base component for tabs, providing the structure, accessibility, and logic for tab navigation.
import { dir } from '@@/bits/current_dir'
import { vSet } from '@@/bits/vue'
import type { VueCssClass } from '@@/types'
import tinykeys from 'tinykeys'
import type { ComponentPublicInstance } from 'vue'
import { computed, onBeforeUnmount, onBeforeUpdate, onMounted, shallowRef } from 'vue'

const {
  accessibilityRole = OzTabsBaseAccessibilityRole.Tabs,
  currentTabKey,
  tabsContainerClasses,
  scrollable = true,
  tabs,
  tabListAriaLabel,
} = defineProps<{
  /**
   * Tabs to display.
   */
  tabs: OzTabsBaseTab[]
  /**
   * Label corresponding to the tab list.
   */
  tabListAriaLabel: string
  /**
   * Key used to detect the current tab.
   */
  currentTabKey?: string
  /**
   * Accommodates the elements and attributes according to the use.
   * If 'Tabs', the component will be structured according to the ARIA tab list specification.
   * If 'Navigation', the component will be structured according to the ARIA navigation specification.
   */
  accessibilityRole?: OzTabsBaseAccessibilityRole
  /**
   * Classes applied to the tabs container.
   */
  tabsContainerClasses?: VueCssClass
  /**
   * Whether the tabs container should be scrollable by `wheel` events.
   * @default true
   */
  scrollable?: boolean
}>()

const emit = defineEmits<{
  (
    e: 'activate-tab',
    event: Event,
    tab: {
      key: string
    },
  ): void
  (
    e: 'arrow-left',
    targetTab: {
      key: string
    },
  ): void
  (
    e: 'arrow-right',
    targetTab: {
      key: string
    },
  ): void
}>()

const handleWheelEvent = (event: WheelEvent): void => {
  if (!scrollable) {
    // Prevent default scrolling behavior
    event.preventDefault()
  }
}

const currentTabId = computed(() => currentTabKey ?? tabs[0]?.key)

// #region Accessibility
const isUsingTabsAccessibilityRole = computed(() => {
  return accessibilityRole === OzTabsBaseAccessibilityRole.Tabs
})

const wrapperElement = computed(() => {
  return accessibilityRole === OzTabsBaseAccessibilityRole.Tabs ? 'div' : 'nav'
})

const wrapperAttributes = computed(() => {
  return accessibilityRole === OzTabsBaseAccessibilityRole.Tabs ? {} : { 'aria-label': tabListAriaLabel }
})

const listAttributes = computed(() => {
  return isUsingTabsAccessibilityRole.value ? { 'aria-label': tabListAriaLabel } : {}
})

const listRole = computed(() => {
  return isUsingTabsAccessibilityRole.value ? 'tablist' : 'list'
})

const listItemRole = computed(() => {
  return isUsingTabsAccessibilityRole.value ? 'presentation' : 'listitem'
})

function computeTabAttributes(tab: OzTabsBaseTab): Record<string, any> {
  return isUsingTabsAccessibilityRole.value
    ? {
        id: tab.key,
        role: 'tab',
        'aria-hidden': tab.ariaHidden === true ? 'true' : undefined,
        'aria-controls': tab.ariaControls,
        'aria-disabled': tab.disabled === true ? 'true' : undefined,
        'aria-selected': currentTabId.value === tab.key ? 'true' : 'false',
        tabindex: currentTabId.value === tab.key ? 0 : -1,
      }
    : {
        role: tab.href ? 'link' : 'button',
      }
}
// #endregion

// #region Keyboard navigation
interface TabRef {
  key: string
  el: HTMLElement
}

const tabsRef = shallowRef<Array<TabRef | null>>(Array(tabs.length).fill(null))

/**
 * Set tab reference for keyboard navigation.
 */
function setTabRef(el: Element | ComponentPublicInstance | null, index: number, tab: OzTabsBaseTab) {
  // Ignore `aria-hidden` elements because they are not focusable.
  // This allows us to skip over hidden tabs during keyboard navigation,
  // as only focusable elements are non-null in `tabsRef`.
  if (el === null || tab.ariaHidden === true) return
  vSet(tabsRef.value, index, { key: tab.key, el: el as HTMLElement })
}

const FOCUSABLE_TAB_SELECTOR = '[role="tab"], [role="link"], [role="button"]'

/**
 * Find the first non-null tab in the array.
 */
function findFirstNonNullTab(): TabRef | null {
  for (const tab of tabsRef.value) {
    if (tab !== null) return tab
  }
  return null
}

/**
 * Find the last non-null tab in the array.
 */
function findLastNonNullTab(): TabRef | null {
  for (let i = tabsRef.value.length - 1; i >= 0; i--) {
    if (tabsRef.value[i] !== null) return tabsRef.value[i]
  }
  return null
}

/**
 * Find the next non-null tab from the given index (with wraparound).
 */
function findNextNonNullTab(currentIndex: number): TabRef | null {
  const startIndex = currentIndex
  let nextIndex = currentIndex

  while (true) {
    nextIndex = (nextIndex + 1) % tabsRef.value.length
    if (tabsRef.value[nextIndex] !== null) return tabsRef.value[nextIndex]

    // Prevent infinite loop
    // `tabsRef` array can contain null values. When looking up the next tab,
    // we skip over null entries.
    // If ALL tabs are null, the while loop would cycle through the entire array
    // repeatedly without finding a valid tab, creating an infinite loop.
    // If we've wrapped around back to start, break
    if (nextIndex === startIndex) return null
  }
}

/**
 * Find the previous non-null tab from the given index (with wraparound).
 */
function findPreviousNonNullTab(currentIndex: number): TabRef | null {
  const startIndex = currentIndex
  let previousIndex = currentIndex

  while (true) {
    // Add tabsRef length to avoid negative index
    previousIndex = (previousIndex - 1 + tabsRef.value.length) % tabsRef.value.length
    if (tabsRef.value[previousIndex] !== null) return tabsRef.value[previousIndex]

    // Prevent infinite loop
    // `tabsRef` array can contain null values. When looking up the previous tab,
    // we skip over null entries.
    // If ALL tabs are null, the while loop would cycle through the entire array
    // repeatedly without finding a valid tab, creating an infinite loop.
    // If we've wrapped around back to start, break
    if (previousIndex === startIndex) return null
  }
}

/**
 * Focus the first tab.
 */
function focusFirstTab(): void {
  const firstTab = findFirstNonNullTab()
  if (firstTab !== null) {
    const focusableEl = firstTab.el.querySelector(FOCUSABLE_TAB_SELECTOR) as HTMLElement
    focusableEl?.focus()
  }
}

/**
 * Focus the last tab.
 */
function focusLastTab(): void {
  const lastTab = findLastNonNullTab()
  if (lastTab !== null) {
    const focusableEl = lastTab.el.querySelector(FOCUSABLE_TAB_SELECTOR) as HTMLElement
    focusableEl?.focus()
  }
}

/**
 * When pressing "Home", focus the first tab.
 */
function handleHomeKey(event: KeyboardEvent): void {
  event.preventDefault()
  focusFirstTab()
}

/**
 * When pressing "End", focus the last tab.
 */
function handleEndKey(event: KeyboardEvent): void {
  event.preventDefault()
  focusLastTab()
}

/**
 * When pressing the left arrow, focus the previous tab.
 * If on first tab, focus the last tab.
 * In RTL mode, left arrow navigates to the next tab instead.
 */
function handleArrowLeft(event: KeyboardEvent): void {
  event.preventDefault()

  const focusedElement = document.activeElement as HTMLElement | null
  if (!focusedElement) return

  // Find which tab contains the currently focused element
  const currentIndex = tabsRef.value.findIndex((tabRef) => tabRef && tabRef.el.contains(focusedElement))

  if (currentIndex === -1) return

  const isRtl = dir() === 'rtl'

  // In RTL, left arrow should go to next tab; in LTR, to previous tab
  const targetTab = isRtl ? findNextNonNullTab(currentIndex) : findPreviousNonNullTab(currentIndex)
  if (targetTab) {
    const focusableEl = targetTab.el.querySelector(FOCUSABLE_TAB_SELECTOR) as HTMLElement
    focusableEl?.focus()
    emit('arrow-left', { key: targetTab.key })
  }
}

/**
 * When pressing the right arrow, focus the next tab.
 * If on last tab, focus the first tab.
 * In RTL mode, right arrow navigates to the previous tab instead.
 */
function handleArrowRight(event: KeyboardEvent): void {
  event.preventDefault()

  const focusedElement = document.activeElement as HTMLElement | null
  if (!focusedElement) return

  // Find which tab contains the currently focused element
  const currentIndex = tabsRef.value.findIndex((tabRef) => tabRef && tabRef.el.contains(focusedElement))

  if (currentIndex === -1) return

  const isRtl = dir() === 'rtl'

  // In RTL, right arrow should go to previous tab; in LTR, to next tab
  const targetTab = isRtl ? findPreviousNonNullTab(currentIndex) : findNextNonNullTab(currentIndex)
  if (targetTab) {
    const focusableEl = targetTab.el.querySelector(FOCUSABLE_TAB_SELECTOR) as HTMLElement
    focusableEl?.focus()
    emit('arrow-right', { key: targetTab.key })
  }
}

onBeforeUpdate(() => {
  // Reset `tabsRef` before any updates to avoid memory leaks.
  // It will be filled again by v-for anyway.
  tabsRef.value = Array(tabs.length).fill(null)
})

const list = shallowRef<HTMLUListElement | null>(null)

let removeKeyboardShortcutsListener = () => {}

onMounted(() => {
  if (list.value) {
    removeKeyboardShortcutsListener = tinykeys(list.value, {
      Home: handleHomeKey,
      End: handleEndKey,
      ArrowLeft: handleArrowLeft,
      ArrowRight: handleArrowRight,
    })
  }
})

onBeforeUnmount(() => {
  removeKeyboardShortcutsListener()
})
// #endregion

const highlightedElement = computed<HTMLElement | null>(() => {
  const tabEl = tabsRef.value.find((tab) => tab?.key === currentTabId.value)?.el
  return tabEl ?? null
})

defineExpose({
  tabsRef,
  currentTabId,
  highlightedElement,
  isUsingTabsAccessibilityRole,
  list,
})
</script>

<script lang="ts">
interface OzTabsBaseTab {
  /**
   * A string to identify the tab.
   */
  key: string
  /**
   * `aria-hidden` attribute.
   */
  ariaHidden?: boolean
  /**
   * `aria-controls` attribute.
   */
  ariaControls?: string
  /**
   * `href` attribute.
   * This also makes the tab a link.
   */
  href?: string
  /**
   * should the tab be disabled or not
   */
  disabled?: boolean
  /**
   * Classes applied to the tab item.
   */
  tabClasses?: VueCssClass
  /**
   * Id for querying tab in tests
   */
  testId?: string
}

/**
 * Accommodates the elements and attributes according to the use.
 * If 'Tabs', the component will be structured according to the ARIA tab list specification.
 * If 'Navigation', the component will be structured according to the ARIA navigation specification.
 */
enum OzTabsBaseAccessibilityRole {
  Tabs = 'Tabs',
  Navigation = 'Navigation',
}

export { OzTabsBaseAccessibilityRole }
export type { OzTabsBaseTab }
</script>

<template>
  <component :is="wrapperElement" v-bind="wrapperAttributes">
    <ul
      ref="list"
      :class="[
        'list-none',
        // Placement
        'flex',
        'justify-between',
        // Spacing
        'm-0',
        'p-0',
        // Custom classes
        tabsContainerClasses,
      ]"
      v-bind="listAttributes"
      :role="listRole"
      @wheel.capture="handleWheelEvent"
    >
      <slot
        name="tabs"
        :tabs="tabs"
        :current-tab-id="currentTabId"
        :set-tab-ref="setTabRef"
        :compute-tab-attributes="computeTabAttributes"
        :list-item-role="listItemRole"
        :activate-tab="(event, tab) => $emit('activate-tab', event, { key: tab.key })"
      >
        <!-- Default implementation when `tabs` slot is not used. -->
        <!-- In most cases, parent components only need to use the `tab` slot -->
        <!-- to customize how each tab is rendered. -->
        <li
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :ref="(el) => setTabRef(el, index, tab)"
          :role="listItemRole"
          :aria-hidden="tab.ariaHidden ? 'true' : undefined"
          :data-testid="tab.testId"
          :data-pw="tab.testId"
          :class="tab.tabClasses"
        >
          <slot
            name="tab"
            :tab="tab"
            :index="index"
            :is-active="currentTabId === tab.key"
            :tab-attributes="computeTabAttributes(tab)"
            :activate-tab="(event) => $emit('activate-tab', event, { key: tab.key })"
          />
        </li>
      </slot>
    </ul>

    <!-- Slot for additional elements like indicators -->
    <slot name="adornment" />
  </component>
</template>
